/**
 * Cutting the translated text into things worth saying (task 6).
 *
 * The translation provider streams its text as fragments of words with no
 * sentence boundaries marked. A second voice cannot speak a fragment — it
 * would read "Buen" and then "os días" — so the text is held until it reads
 * as a phrase and only then handed on.
 *
 * Three things end a phrase, in the order they usually happen:
 *
 *   - sentence punctuation followed by a space, which is the common case and
 *     costs no waiting at all;
 *   - a pause in the stream, handled by the caller with a timer and `drain()`,
 *     because speech without punctuation still ends;
 *   - length, as a backstop, so a long run-on sentence starts being heard
 *     before it finishes. It cuts at the last comma if there is one, else at
 *     the last space, never inside a word.
 *
 * Cutting sooner was tried twice on staging and taken back both times: a
 * shorter pause (see `PAUSE_MS`), and a cut at every comma with 24
 * characters before it. Both meant more, shorter phrases — 58 and 30 where
 * the same amount of speech had been 21 — and the second voice fell further
 * behind the more phrases there were, 5.5 s and 3.6 s on average against
 * 0.35 s, growing through the call. Every phrase costs more than its words.
 *
 * Everything here is pure and has no clock, which is what lets it be tested
 * without timers.
 */

/**
 * A phrase with no ending is spoken after this long without a new word.
 *
 * 700 ms, and not shorter: 400 ms was tried and made things much worse. The
 * translation provider's text arrives in bursts with gaps longer than that
 * inside a sentence, so sentences were cut into scraps — 58 phrases of about
 * 18 characters where the same amount of speech had been 21 — and each
 * scrap is a turn the voice provider starts and ends with its own margin.
 * Those margins queued up behind one another: the second voice fell from
 * 0.35 s behind the built-in one to 5.5 s behind on average, 6.7 s by the end.
 */
export const PAUSE_MS = 700;

/** Past this many characters a phrase is spoken even without an ending. */
export const MAX_PHRASE_CHARS = 160;

// A sentence end: one or more of these, any closing quotes or brackets, then
// whitespace. The whitespace is what proves the sentence is over rather than
// "3." being the first half of "3.5".
const SENTENCE_END = /[.!?…。？！؟]+["'»”’)\]]*\s/g;

export class PhraseSplitter {
  #buffer = '';
  readonly #max: number;

  constructor(max = MAX_PHRASE_CHARS) {
    this.#max = max;
  }

  /** Whatever is held and not yet said. */
  get pending(): string {
    return this.#buffer;
  }

  /** Adds a fragment; returns the phrases it completed, in order. */
  push(fragment: string): string[] {
    this.#buffer += fragment;
    const out: string[] = [];

    const end = lastSentenceEnd(this.#buffer);
    if (end > 0) {
      out.push(...split(this.#buffer.slice(0, end)));
      this.#buffer = this.#buffer.slice(end);
    }

    while (this.#buffer.length > this.#max) {
      const cut = cutPoint(this.#buffer, this.#max);
      out.push(this.#buffer.slice(0, cut));
      this.#buffer = this.#buffer.slice(cut);
    }

    return out.map((p) => p.trim()).filter((p) => p !== '');
  }

  /** Everything held, as one phrase — for when the speaker has paused. */
  drain(): string {
    const rest = this.#buffer.trim();
    this.#buffer = '';
    return rest;
  }
}

/** Index just past the last sentence end in `text`, or 0 if there is none. */
function lastSentenceEnd(text: string): number {
  let end = 0;
  for (const m of text.matchAll(SENTENCE_END)) end = m.index + m[0].length;
  return end;
}

/** Several finished sentences arrive in one fragment only rarely; keep them apart. */
function split(text: string): string[] {
  const out: string[] = [];
  let from = 0;
  for (const m of text.matchAll(SENTENCE_END)) {
    const to = m.index + m[0].length;
    out.push(text.slice(from, to));
    from = to;
  }
  if (from < text.length) out.push(text.slice(from));
  return out;
}

/** Where to cut an over-long phrase: after the last comma, else the last space. */
function cutPoint(text: string, max: number): number {
  const head = text.slice(0, max);
  const comma = Math.max(head.lastIndexOf(', '), head.lastIndexOf('، '));
  if (comma > 0) return comma + 2;
  const space = head.lastIndexOf(' ');
  return space > 0 ? space + 1 : max;
}

/**
 * 16-bit little-endian mono PCM to the floats Web Audio plays.
 *
 * The provider's chunks do not respect sample boundaries — a chunk can end on
 * the first byte of a sample — so an odd trailing byte is handed back to be
 * put in front of the next chunk instead of being dropped, which would shift
 * every sample after it and turn the voice into noise.
 */
export function decodePcm16(
  bytes: Uint8Array,
  carry: Uint8Array | null = null,
): { samples: Float32Array<ArrayBuffer>; carry: Uint8Array | null } {
  let data = bytes;
  if (carry && carry.length > 0) {
    data = new Uint8Array(carry.length + bytes.length);
    data.set(carry, 0);
    data.set(bytes, carry.length);
  }
  const whole = data.length - (data.length % 2);
  const samples = new Float32Array(whole / 2);
  const view = new DataView(data.buffer, data.byteOffset, whole);
  for (let i = 0; i < samples.length; i += 1) {
    samples[i] = view.getInt16(i * 2, true) / 32768;
  }
  return { samples, carry: whole < data.length ? data.slice(whole) : null };
}
